import { logger } from "@/utils/logger";

import React, { useRef, useEffect, forwardRef, useCallback, useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Sparkles } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import MessageBubble from '../MessageBubble';
import TypingIndicator from '../TypingIndicator';
import PromptMenu from '../PromptMenu';
import { WaitingVideoMessage } from './WaitingVideoMessage';
import { MessageClassificationResult } from '../messages/MessageClassificationResult';
import StreamingIntroMessage from '../messages/StreamingIntroMessage';
import MessageContent from '../messages/MessageContent';
import { useLanguage } from '@/contexts/LanguageContext';
import { useFormContext } from '@/contexts/FormContext';
import { useDealerWelcomeMessage } from '@/hooks/useDealerWelcomeMessage';
import { DealerManager } from '@/utils/dealerManager';
import { useCertificationStyles } from '@/hooks/useCertificationStyles';
// Note: Message classification is now handled server-side in edge functions

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  aiResponseReceivedAt?: Date;
  isRetryable?: boolean;
  skipIntroAndClosing?: boolean;
}

interface StreamingIntroState {
  displayedIntro: string;
  isTyping: boolean;
  isComplete: boolean;
  isStreaming: boolean;
  isEnabled: boolean;
  streamStartTime?: Date | null;
}

interface ChatMessagesProps {
  messages: Message[];
  isLoading: boolean;
  isSearching?: boolean;
  isRestoring?: boolean;
  sessionId: string;
  onSendMessage: (message: string) => void;
  inputValue: string;
  setInputValue: (value: string) => void;
  onFormExpand?: (formType: string) => void;
  onRetryLastRequest?: () => Promise<void>;
  waitingVideoData?: {
    Waiting_Youtube_Video_url: string;
    Waiting_Message: string;
  } | null;
  onCopyConversation?: () => void;
  streamingIntro?: StreamingIntroState;
}

const ChatMessages = forwardRef<HTMLDivElement, ChatMessagesProps>(({
  messages,
  isLoading,
  isSearching = false,
  isRestoring = false,
  sessionId,
  onSendMessage,
  inputValue,
  setInputValue,
  onFormExpand,
  onRetryLastRequest,
  waitingVideoData,
  onCopyConversation,
  streamingIntro
}, ref) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const previousMessageCountRef = useRef(messages.length);
  const [smsModalEngaged, setSmsModalEngaged] = useState(false);
  const lastWaitingVideoDataRef = useRef(waitingVideoData);
  const { t } = useLanguage();
  const { setActiveForm } = useFormContext();
  const { welcomeMessage, isLoading: isLoadingWelcome } = useDealerWelcomeMessage();
  const { styles } = useCertificationStyles();
  // Keep track of last valid waiting video data for when user is engaged
  useEffect(() => {
    if (waitingVideoData?.Waiting_Youtube_Video_url) {
      lastWaitingVideoDataRef.current = waitingVideoData;
    }
  }, [waitingVideoData]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'instant', block: 'end' });
  };

  useEffect(() => {
    // Only scroll if messages changed (new message added)
    if (messages.length > previousMessageCountRef.current) {
      setTimeout(() => scrollToBottom(), 0);
    }
    
    previousMessageCountRef.current = messages.length;
  }, [messages]);

  // Scroll to bottom when restoration is complete
  useEffect(() => {
    if (!isRestoring && messages.length > 0) {
      // For streaming dealers, don't auto-scroll after restoration
      const isStreaming = DealerManager.isStreamEnabled();
      
      if (!isStreaming) {
        // Add a small delay to ensure DOM is fully rendered
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    }
  }, [isRestoring, messages.length]);

  // Smoothly scroll to show streaming content when it starts appearing
  useEffect(() => {
    if (streamingIntro?.isStreaming && streamingIntro?.displayedIntro && !streamingIntro?.isComplete) {
      // Smooth scroll to bottom to reveal streaming content
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [streamingIntro?.isStreaming, streamingIntro?.displayedIntro, streamingIntro?.isComplete]);

  const handleOfferButtonClick = async () => {
    const { hasVehicleContext } = await import('@/utils/vehicleContextHelpers');
    if (!hasVehicleContext()) {
      const { toast } = await import('sonner');
      toast.error('Please select a vehicle before requesting an offer.');
      return;
    }
    setActiveForm('Offer');
  };

  const handleFormExpand = useCallback(async (formType: string) => {
    logger.log('🔀 UI: handleFormExpand called with:', formType);
    
    // Handle case-insensitive form type matching first
    const formTypeLower = formType.toLowerCase();
    
    // Special handling for Trade form - trigger trade action instead of opening form
    if (formTypeLower === 'trade') {
      const { openTradeForm } = await import('@/utils/tradeFormHandler');
      const currentLanguage = document.documentElement.lang === 'fr' ? 'fr' : 'en';
      await openTradeForm(currentLanguage);
      return;
    }
    
    // Special handling for Service form - check if dealer uses external Site mode
    if (formTypeLower === 'service') {
      const dealer = DealerManager.getCurrentDealer();
      
      if (dealer?.service_form_mode === 'Site') {
        // Get the service form URL based on current language
        const { getServiceFormUrl } = await import('@/utils/cookieUtils');
        const currentLang = document.documentElement.lang === 'fr' ? 'fr' : 'en';
        const siteFormUrl = currentLang === 'fr' ? dealer.service_form_url_fr : dealer.service_form_url_en;
        const fallbackUrl = !siteFormUrl ? getServiceFormUrl(currentLang) : siteFormUrl;
        const externalServiceFormUrl = fallbackUrl || siteFormUrl;
        
        if (externalServiceFormUrl) {
          window.open(externalServiceFormUrl, '_blank');
          return;
        }
        // No external URL available — fall through and open the in-chat Service form
        logger.warn('⚠️ service_form_mode=Site but no external URL configured; opening in-chat Service form as fallback');
      }
    }
    
    // Check for recent form submission (2-minute window)
    try {
      const SecureStorage = (await import('@/utils/secureStorage')).default;
      const lastFormSubmissionTime = await SecureStorage.getItem('lastFormSubmissionTime');
      
      if (lastFormSubmissionTime) {
        const timeDiff = Date.now() - lastFormSubmissionTime;
        const twoMinutesInMs = 2 * 60 * 1000;
        
        if (timeDiff < twoMinutesInMs) {
          logger.log('🔀 UI: Blocked form open - recent submission detected');
          return; // Don't open form if there was a recent submission
        }
      }
    } catch (error) {
      // Silent error handling
    }
    
    // Map lowercase form types to their correct casing
    const formTypeMap: { [key: string]: string } = {
      'financev1': 'FinanceV1',
      'finance': 'Finance',
      'appointment': 'Appointment',
      'service': 'Service',
      'offer': 'Offer',
      'trade': 'Trade',
      'tradev2': 'TradeV2',
      'contact': 'Contact'
    };
    
    const mappedFormType = formTypeMap[formTypeLower] || formType;

    if (mappedFormType === 'Offer') {
      const { hasVehicleContext } = await import('@/utils/vehicleContextHelpers');
      if (!hasVehicleContext()) {
        const { toast } = await import('sonner');
        toast.error('Please select a vehicle before requesting an offer.');
        logger.warn('🚫 Offer form blocked from handleFormExpand: no vehicle in context');
        return;
      }
    }

    logger.log('🔀 UI: Calling setActiveForm with:', mappedFormType);
    // Type assertion since we know these are valid ExtendedFormType values
    setActiveForm(mappedFormType as any);
    
    // Call parent handler if provided
    if (onFormExpand) {
      onFormExpand(mappedFormType);
    }
  }, [setActiveForm, onFormExpand]);

  // Note: Message classification is now handled server-side in edge functions

  return (
    <ScrollArea ref={ref} className="flex-1 min-w-0 pt-3 pl-4 pr-2 pb-4 d2cbot-bg-white d2cbot-bg-gradient-none">
      <div className="d2cbot-message-list space-y-3 pr-2 min-w-0 d2cbot-bg-white d2cbot-bg-gradient-none">

        {/* Welcome message - only show if no messages, not restoring, and no existing session */}
        {messages.length === 0 && !isRestoring && (
          <div className="text-center animate-fade-in">
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-3 text-left d2cbot-bot_border d2cbot-bot_border-gray-200 animate-scale-in group hover:bg-gradient-to-br hover:from-gray-100 hover:to-gray-200 transition-all duration-300">
              <div className="flex items-center space-x-2 mb-2">
                <span className="font-semibold text-xs text-gray-800">{t('chat.agent.name')}</span>
                <span className="text-xs text-gray-500">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}</span>
                <Sparkles className="w-3 h-3 text-yellow-500 animate-pulse" />
              </div>
              <p className="text-gray-700 mb-1 text-sm">
                {isLoadingWelcome ? '' : welcomeMessage}
              </p>
            </div>
          </div>
        )}
        
        {/* Prompt Menu - only show when no messages, not restoring, and no existing session */}
        {messages.length === 0 && !isRestoring && (
          <PromptMenu 
            onPromptSelect={onSendMessage} 
            inputValue={inputValue}
            setInputValue={setInputValue}
            onFormExpand={handleFormExpand}
          />
        )}
        
        {/* Show loading indicator during restoration */}
        {isRestoring && (
          <div className="text-center py-4">
            <div className="text-sm text-gray-500">Restoring conversation...</div>
          </div>
        )}
        
        {/* Show waiting video at the top if available and loading, or if SMS modal is engaged */}
        {((waitingVideoData && waitingVideoData.Waiting_Youtube_Video_url) || 
          (smsModalEngaged && lastWaitingVideoDataRef.current?.Waiting_Youtube_Video_url)) && (
          <div className="animate-in fade-in-50 duration-500">
            <WaitingVideoMessage 
              videoUrl={(waitingVideoData || lastWaitingVideoDataRef.current)!.Waiting_Youtube_Video_url}
              message={(waitingVideoData || lastWaitingVideoDataRef.current)!.Waiting_Message}
              userQuestion={messages.filter(m => m.isUser).pop()?.content || ''}
              aiResponse={messages.filter(m => !m.isUser).pop()?.content || ''}
              onSMSModalEngaged={setSmsModalEngaged}
            />
          </div>
        )}
        
        {messages.map((message, index) => {
          // Find the last assistant message index (for UI affordances like copy/retry)
          const lastAssistantIndex = messages.reduce((lastIdx, msg, idx) =>
            !msg.isUser ? idx : lastIdx, -1);
          const isLastAssistantMessage = !message.isUser && index === lastAssistantIndex;

          // IMPORTANT: Skip rendering the last message when the streaming block is handling it.
          // This must use the SAME condition as shouldShowStreamingBlock to prevent duplication.
          const isLastMessage = index === messages.length - 1;
          const hasAdditionalContent = message.skipIntroAndClosing && !message.isUser;
          
          // Check if the streaming block will render this message
          const streamingBlockWillRender = isLastMessage && 
            hasAdditionalContent &&
            streamingIntro?.isEnabled && 
            streamingIntro?.displayedIntro && 
            (streamingIntro.isStreaming || hasAdditionalContent);
          
          if (streamingBlockWillRender) {
            return null; // Streaming block handles this message
          }

          // When rendering messages not handled by streaming block,
          // always show full content (intro/closing included).
          const messageForRender = message.skipIntroAndClosing
            ? { ...message, skipIntroAndClosing: false }
            : message;

          return (
            <div 
              key={message.id}
              ref={index === messages.length - 1 ? lastMessageRef : undefined}
            >
              <MessageBubble 
                message={messageForRender}
                sessionId={sessionId}
                onSendMessage={onSendMessage}
                onRetryLastRequest={onRetryLastRequest}
                isLastAssistantMessage={isLastAssistantMessage}
                onCopyConversation={onCopyConversation}
              />
              
              {/* Note: Message classification is now handled server-side */}
            </div>
          );
        })}
        
        {/* Unified streaming message - combines intro text and additional content (cards, reply options) as one message */}
        {(() => {
          const lastMessage = messages[messages.length - 1];
          const hasAdditionalContent = lastMessage?.skipIntroAndClosing && !lastMessage.isUser;
          
          // Only show unified streaming block when:
          // 1. Streaming is actively happening (isStreaming), OR
          // 2. The last message was streamed (skipIntroAndClosing) and we have displayedIntro
          const shouldShowStreamingBlock = streamingIntro?.isEnabled && streamingIntro?.displayedIntro && 
            (streamingIntro.isStreaming || hasAdditionalContent);
          
          if (!shouldShowStreamingBlock) return null;
          
          return (
            <div className="flex justify-start animate-fade-in d2cbot-bg-white d2cbot-bg-gradient-none" ref={hasAdditionalContent ? lastMessageRef : undefined}>
              <div className="flex flex-row items-start space-x-2 min-w-0 max-w-[95%] d2cbot-bg-white d2cbot-bg-gradient-none">
                <div className="flex flex-col w-full min-w-0">

                  {/* AI Agent header with timestamp */}
                  <div className="flex items-center justify-start mb-1 w-full">
                    <span className="text-xs font-semibold text-gray-600 mr-2">
                      {t("chat.agent.name")}
                    </span>
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                            {(streamingIntro?.streamStartTime ?? (hasAdditionalContent ? lastMessage.timestamp : undefined))?.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) || ""}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-xs z-[10001]">
                          {(streamingIntro?.streamStartTime ?? (hasAdditionalContent ? lastMessage.timestamp : undefined))?.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) || ""}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <Sparkles className="w-3 h-3 text-yellow-500 ml-1 animate-pulse" />
                  </div>
                  
                  {/* Combined message bubble */}
                  <div 
                    className="bg-gray-100 p-3 max-w-full break-words overflow-hidden"
                    style={{ borderRadius: `${styles.corner_radius}px` }}
                  >
                    {/* Streaming intro text */}
                    <StreamingIntroMessage
                      displayedText={streamingIntro.displayedIntro}
                      isTyping={streamingIntro.isTyping}
                      isComplete={streamingIntro.isComplete}
                    />
                    
                    {/* Loading indicator - shows after intro finishes typing but before full content arrives */}
                    {/* Condition: intro done typing (!isTyping) + still streaming (isStreaming) + no additional content yet */}
                    {!streamingIntro.isTyping && streamingIntro.isStreaming && !hasAdditionalContent && (
                      <div className="mt-4 pt-3 border-t border-gray-200/50 animate-fade-in">
                        <div className="flex items-center gap-3">
                          {/* Animated shimmer bar */}
                          <div className="flex-1 h-1.5 bg-gray-200/80 rounded-full overflow-hidden">
                            <div 
                              className="h-full w-full bg-gradient-to-r from-gray-200 via-primary/50 to-gray-200 rounded-full animate-shimmer"
                            />
                          </div>
                          <span className="text-xs text-gray-500 font-medium animate-pulse">{t('chat.loadingMore')}</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Additional content (cards, reply options) - rendered inline */}
                    {hasAdditionalContent && (
                      <div className="mt-2">
                        <MessageContent 
                          content={lastMessage.content}
                          isUser={false}
                          sessionId={sessionId}
                          onSendMessage={onSendMessage}
                          messageTimestamp={lastMessage.timestamp}
                          skipIntroAndClosing={true}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
        
        {isLoading && !streamingIntro?.displayedIntro && <TypingIndicator isSearching={isSearching} />}
        <div ref={messagesEndRef} />
      </div>
    </ScrollArea>
  );
});

ChatMessages.displayName = 'ChatMessages';

export default ChatMessages;
